import { CodeMirrorView } from "@repo/fika-blog/src/lib/codeView.js"
import {
  iframeTemplate,
  type SnippetTranslations,
} from "@repo/fika-blog/src/nodes/snippet.js"
import type { NodeViewRendererProps } from "@repo/fika-blog/src/tiptap/core"
import type { Node } from "@repo/fika-blog/src/tiptap/pm/model"
import type { EditorView } from "@repo/fika-blog/src/tiptap/pm/view"
import { http } from "@/data/http"
import {
  ANIMATION_DURATION,
  snippetButtonGroup,
  snippetCodeButton,
  snippetCodeSection,
  snippetContent,
  snippetDeleteButton,
  snippetIframe,
  snippetPreviewButton,
  snippetPreviewSection,
  snippetPromptAnimation,
  snippetPromptButton,
  snippetPromptSection,
  snippetPromptTextarea,
} from "./snippet-view.css.js"

const SNIPPET_VIEW_MODES = {
  code: "code",
  preview: "preview",
  both: "both",
} as const
type ViewMode = (typeof SNIPPET_VIEW_MODES)[keyof typeof SNIPPET_VIEW_MODES]

export class SnippetView {
  node: Node
  view: EditorView
  getPos: NodeViewRendererProps["getPos"]
  codeView!: CodeMirrorView
  dom!: HTMLElement
  viewMode: ViewMode
  $preview!: HTMLIFrameElement
  $codeButton!: HTMLButtonElement
  $previewButton!: HTMLButtonElement
  $deleteButton!: HTMLButtonElement
  $promptTextarea!: HTMLTextAreaElement
  $promptButton!: HTMLButtonElement
  $promptAnimation!: HTMLElement
  $content!: HTMLElement
  $promptSection!: HTMLElement
  translations: SnippetTranslations

  constructor(
    node: Node,
    view: EditorView,
    getPos: NodeViewRendererProps["getPos"],
    translations: SnippetTranslations,
  ) {
    this.node = node
    this.view = view
    this.getPos = getPos
    this.translations = translations

    this.viewMode = this.node.attrs.viewMode || "preview"

    this.dom = document.createElement("div")

    this.$promptSection = document.createElement("div")
    this.$promptSection.className = snippetPromptSection({
      entering: true,
    })
    this.$promptAnimation = document.createElement("div")
    this.$promptAnimation.className = snippetPromptAnimation({
      active: false,
    })

    this.$promptTextarea = document.createElement("textarea")
    this.$promptTextarea.className = snippetPromptTextarea
    this.$promptTextarea.setAttribute(
      "placeholder",
      this.translations?.placeholder || "Describe what you want to create...",
    )
    this.$promptTextarea.setAttribute("rows", "3")
    this.$promptTextarea.addEventListener("keydown", e => {
      e.stopPropagation()
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        this.handlePromptSubmit()
      }
    })

    this.$promptButton = document.createElement("button")
    this.$promptButton.className = snippetPromptButton
    this.$promptButton.addEventListener("click", () =>
      this.handlePromptSubmit(),
    )

    this.$deleteButton = document.createElement("button")
    this.$deleteButton.innerHTML = `${icon(
      "delete",
      '<path d="M10 11v6"/><path d="M14 11v6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>',
    )}<span>${this.translations?.delete || "delete"}</span>`
    this.$deleteButton.className = snippetDeleteButton

    const $buttonGroup = document.createElement("div")
    $buttonGroup.className = snippetButtonGroup
    $buttonGroup.appendChild(this.$deleteButton)
    $buttonGroup.appendChild(this.$promptButton)
    $buttonGroup.appendChild(this.$promptAnimation)

    this.$promptSection.appendChild(this.$promptTextarea)
    this.$promptSection.appendChild($buttonGroup)

    this.$content = document.createElement("div")
    this.$content.className = snippetContent({
      viewMode: this.viewMode,
    })

    const $codeSection = document.createElement("div")
    $codeSection.className = snippetCodeSection

    this.$codeButton = document.createElement("button")
    this.$codeButton.innerHTML = icon(
      "code",
      '<rect width="18" height="18" x="3" y="3" rx="2"/><path d="M9 3v18"/><path d="m14 9 3 3-3 3"/>',
    )
    this.$codeButton.className = snippetCodeButton

    const $codeEditor = document.createElement("pre")
    const $code = document.createElement("code")
    $codeEditor.appendChild($code)

    $codeSection.appendChild(this.$codeButton)
    $codeSection.appendChild($codeEditor)

    const $previewSection = document.createElement("div")
    $previewSection.className = snippetPreviewSection

    this.$previewButton = document.createElement("button")
    this.$previewButton.className = snippetPreviewButton

    this.$preview = document.createElement("iframe")
    this.$preview.setAttribute("credentialless", "true")
    this.$preview.setAttribute("sandbox", "allow-scripts")
    this.$preview.className = snippetIframe
    this.$preview.addEventListener("load", () => {
      const doc = this.$preview.contentDocument
      if (!doc) return

      const body = doc.body

      const updateHeight = () => {
        this.$preview.style.height = ""
        this.$preview.style.height = `${body.scrollHeight}px`
      }

      const ro = new ResizeObserver(updateHeight)
      ro.observe(body)
      setInterval(updateHeight, 200)

      updateHeight()
    })

    $previewSection.appendChild(this.$previewButton)
    $previewSection.appendChild(this.$preview)

    this.codeView = new CodeMirrorView(
      this.node,
      this.view,
      this.getPos,
      $code,
      () => {
        this.handleCodeChange()
      },
      this.node.attrs?.language || "html",
    )

    this.setupEventHandlers()

    this.$content.appendChild($codeSection)
    this.$content.appendChild($previewSection)

    this.dom.appendChild(this.$promptSection)

    // Only append content section if there's content
    if (this.node.textContent && this.node.textContent.trim().length > 0) {
      this.dom.appendChild(this.$content)
    }

    this.updatePreview()
    this.updateViewMode(this.viewMode)
    this.updateButtonIcon()
    this.updatePromptUI()
  }

  handleCodeChange() {
    this.updatePreview()

    const currentCode = this.codeView.cm.state.doc.toString()
    const pos = this.getPos()
    if (typeof pos !== "number") return

    this.view.dispatch(
      this.view.state.tr.replaceWith(
        pos + 1,
        pos + this.node.nodeSize - 1,
        this.view.state.schema.text(currentCode),
      ),
    )

    this.updateContentVisibility(currentCode.trim().length > 0)
    this.updatePromptUI()
  }

  updateContentVisibility(hasContent: boolean) {
    const isContentInDom = this.dom.contains(this.$content)

    if (hasContent && !isContentInDom) {
      this.dom.appendChild(this.$content)
    } else if (!hasContent && isContentInDom) {
      this.dom.removeChild(this.$content)
    }
  }

  async handlePromptSubmit() {
    const userPrompt = this.$promptTextarea.value.trim()
    if (!userPrompt) return

    // Get current code if it exists
    const currentCode = this.codeView.cm.state.doc.toString().trim()
    const isUpdate = currentCode.length > 0

    // If there's existing code, prepend it to the prompt for context
    const prompt = isUpdate
      ? `Current code:\n\`\`\`html\n${currentCode}\n\`\`\`\n\nUser request:\n${userPrompt}`
      : userPrompt

    this.$promptButton.disabled = true
    this.$promptButton.innerHTML = `${icon(
      "sparkles",
      '<path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/><path d="M20 3v4"/><path d="M22 5h-4"/><path d="M4 17v2"/><path d="M5 18H3"/>',
    )}<span>${
      isUpdate
        ? this.translations?.buttonUpdating || "Updating..."
        : this.translations?.buttonCreating || "Creating..."
    }</span>`

    this.$promptSection.className = snippetPromptSection({
      generating: true,
    })
    this.$promptAnimation.className = snippetPromptAnimation({
      active: true,
    })

    try {
      const response = await http("/api/snippets", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ prompt }),
      })

      if (!response.ok) {
        throw new Error("Failed to generate snippet")
      }

      const data = (await response.json()) as { html: string }
      const html = data.html

      if (this.codeView.cm) {
        this.codeView.cm.dispatch({
          changes: {
            from: 0,
            to: this.codeView.cm.state.doc.length,
            insert: html,
          },
        })

        this.handleCodeChange()
        this.$promptTextarea.value = ""
      }
      this.$promptSection.className = snippetPromptSection({
        generated: true,
      })
    } catch (error) {
      console.error("Error generating snippet:", error)
    } finally {
      this.$promptButton.disabled = false
      this.$promptAnimation.className = snippetPromptAnimation({
        active: false,
      })
      this.updatePromptUI()
    }
  }

  updatePromptUI() {
    const currentCode = this.codeView.cm.state.doc.toString().trim()
    const isUpdate = currentCode.length > 0

    // Update button text
    this.$promptButton.innerHTML = `${icon(
      "sparkles",
      '<path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/><path d="M20 3v4"/><path d="M22 5h-4"/><path d="M4 17v2"/><path d="M5 18H3"/>',
    )}<span>${
      isUpdate
        ? this.translations?.buttonUpdate || "update"
        : this.translations?.buttonCreate || "create"
    }</span>`

    // Update placeholder
    this.$promptTextarea.setAttribute(
      "placeholder",
      isUpdate
        ? this.translations?.placeholderUpdate ||
            "Describe what you want to change..."
        : this.translations?.placeholder ||
            "Describe what you want to create...",
    )
  }

  updateButtonIcon() {
    const isBothMode = this.viewMode === SNIPPET_VIEW_MODES.both
    const newIcon = isBothMode
      ? icon(
          "maximize",
          '<path d="M15 3h6v6"/><path d="m21 3-7 7"/><path d="m3 21 7-7"/><path d="M9 21H3v-6"/>',
        )
      : icon(
          "minimize",
          '<path d="m14 10 7-7"/><path d="M20 10h-6V4"/><path d="m3 21 7-7"/><path d="M4 14h6v6"/>',
        )
    this.$previewButton.innerHTML = newIcon
    this.$codeButton.innerHTML = newIcon
  }

  setupEventHandlers() {
    this.$codeButton.addEventListener("click", () => {
      const newMode =
        this.viewMode === SNIPPET_VIEW_MODES.code
          ? SNIPPET_VIEW_MODES.both
          : SNIPPET_VIEW_MODES.code
      this.updateViewMode(newMode)
    })
    this.$previewButton.addEventListener("click", () => {
      const newMode =
        this.viewMode === SNIPPET_VIEW_MODES.preview
          ? SNIPPET_VIEW_MODES.both
          : SNIPPET_VIEW_MODES.preview
      this.updateViewMode(newMode)
    })
    this.$deleteButton.addEventListener("click", () => this.deleteNode())
  }

  updateViewMode(mode: ViewMode) {
    this.viewMode = mode
    this.$content.className = snippetContent({
      viewMode: mode,
    })

    const pos = this.getPos()
    if (typeof pos !== "number") return
    const attrs = { ...this.node.attrs, viewMode: mode }
    this.view.dispatch(this.view.state.tr.setNodeMarkup(pos, undefined, attrs))

    this.updateButtonIcon()
  }

  updatePreview() {
    const code = this.codeView.cm.state.doc.toString()
    const html = iframeTemplate(code)

    const blob = new Blob([html], { type: "text/html" })
    const url = URL.createObjectURL(blob)
    this.$preview.src = url

    setTimeout(() => URL.revokeObjectURL(url), 100)
  }

  deleteNode() {
    this.$promptSection.className = snippetPromptSection({
      deleting: true,
    })
    setTimeout(() => {
      const pos = this.getPos()

      if (pos !== null && pos !== undefined) {
        const tr = this.view.state.tr.deleteRange(pos, pos + this.node.nodeSize)
        this.view.dispatch(tr)
        this.view.focus()
      }
    }, ANIMATION_DURATION)
  }

  update(node: any) {
    if (node.type !== this.node.type) return false

    if (node.attrs.viewMode !== this.node.attrs.viewMode) {
      this.viewMode = node.attrs.viewMode
      this.$content.className = snippetContent({
        viewMode: this.viewMode,
      })
    }

    // Update content visibility
    const hasContent = node.textContent && node.textContent.trim().length > 0
    this.updateContentVisibility(hasContent)

    this.node = node
    return this.codeView.update(node)
  }

  selectNode() {
    this.codeView.selectNode()
    setTimeout(() => {
      if (this.codeView.cm && !this.codeView.cm.hasFocus) {
        this.codeView.cm.focus()
      }
    }, 20)
  }

  stopEvent() {
    return this.codeView.stopEvent()
  }

  destroy() {
    this.codeView.destroy()
  }
}

function icon(title: string, content: string) {
  return `<svg title="${title}" viewBox="0 0 24 24" width="18" height="18" stroke-width="2" stroke-linecap="round" fill="none" stroke="currentColor">
  <title>${title}</title>
  ${content}
</svg>`
}
